import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useService } from '../../../../shared/hooks/useService';
import { useAuth } from '../../../../shared/hooks/useAuth';
import './styles.css';

const ACTIVITY_COLORS = {
    running: '#FC4C02',
    cycling: '#2D86C8',
    hiking: '#22A844',
    swimming: '#0AAFDB',
    other: '#888888',
};

function routesToGeoJSON(routes, activeUser) {
    return {
        type: 'FeatureCollection',
        features: routes
            .filter(r => !activeUser || r.username === activeUser)
            .map(r => {
                let coordinates = [];
                try {
                    const latlngs = polyline.decode(r.routePolyline);
                    coordinates = latlngs.map(([lat, lng]) => [lng, lat]);
                } catch (_) {
                    return null;
                }
                if (coordinates.length < 2) return null;
                return {
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates },
                    properties: {
                        username: r.username,
                        displayName: `${r.firstName} ${r.lastName}`.trim(),
                        activityType: r.activityType,
                        date: r.date,
                        color: ACTIVITY_COLORS[r.activityType] || ACTIVITY_COLORS.other,
                    },
                };
            })
            .filter(Boolean),
    };
}

const MapPage = () => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const service = useService();
    const { user } = useAuth();

    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeUser, setActiveUser] = useState(null);
    const [mapReady, setMapReady] = useState(false);

    const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

    useEffect(() => {
        service.getActivityRoutes()
            .then(data => {
                setRoutes(data);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message || 'Failed to load routes');
                setLoading(false);
            });
    }, [service]);

    useEffect(() => {
        if (!token || map.current) return;

        mapboxgl.accessToken = token;
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/dark-v11',
            center: [10, 20],
            zoom: 2,
        });

        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

        map.current.on('load', () => {
            map.current.addSource('routes', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
            });

            map.current.addLayer({
                id: 'routes-line',
                type: 'line',
                source: 'routes',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 2,
                    'line-opacity': 0.75,
                },
            });

            map.current.addLayer({
                id: 'routes-line-hover',
                type: 'line',
                source: 'routes',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 4,
                    'line-opacity': 1,
                },
                filter: ['==', 'username', ''],
            });

            // Invisible wide layer used purely as a hit target so hover works
            // without pixel-perfect aim on a 2px line.
            map.current.addLayer({
                id: 'routes-hit',
                type: 'line',
                source: 'routes',
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: { 'line-width': 20, 'line-opacity': 0 },
            });

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                offset: 12,
                className: 'map-popup',
            });

            map.current.on('mouseenter', 'routes-hit', e => {
                map.current.getCanvas().style.cursor = 'pointer';
                const props = e.features[0].properties;
                map.current.setFilter('routes-line-hover', ['==', 'username', props.username]);
                popup
                    .setLngLat(e.lngLat)
                    .setHTML(
                        `<strong>${props.displayName}</strong><br/>` +
                        `${props.activityType} · ${props.date ? props.date.slice(0, 10) : ''}`
                    )
                    .addTo(map.current);
            });

            map.current.on('mousemove', 'routes-hit', e => {
                popup.setLngLat(e.lngLat);
            });

            map.current.on('mouseleave', 'routes-hit', () => {
                map.current.getCanvas().style.cursor = '';
                map.current.setFilter('routes-line-hover', ['==', 'username', '']);
                popup.remove();
            });

            setMapReady(true);
        });

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [token]);

    // Update map data whenever routes or activeUser changes
    useEffect(() => {
        if (!mapReady || !map.current) return;

        const geojson = routesToGeoJSON(routes, activeUser);
        map.current.getSource('routes')?.setData(geojson);

        // Fit bounds to visible routes
        if (geojson.features.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            geojson.features.forEach(f => {
                f.geometry.coordinates.forEach(coord => bounds.extend(coord));
            });
            if (!bounds.isEmpty()) {
                map.current.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 800 });
            }
        }
    }, [mapReady, routes, activeUser]);

    const uniqueUsers = [...new Map(routes.map(r => [r.username, r])).values()];

    if (!token) {
        return (
            <div className="map-page-error">
                <p>Set <code>REACT_APP_MAPBOX_ACCESS_TOKEN</code> in your environment to enable the world map.</p>
            </div>
        );
    }

    return (
        <div className="map-page">
            <div className="map-controls">
                <div className="map-filter-pills">
                    <button
                        className={`map-pill${activeUser === null ? ' active' : ''}`}
                        onClick={() => setActiveUser(null)}
                    >
                        All athletes
                    </button>
                    {uniqueUsers.map(u => (
                        <button
                            key={u.username}
                            className={`map-pill${activeUser === u.username ? ' active' : ''}`}
                            onClick={() => setActiveUser(prev => prev === u.username ? null : u.username)}
                        >
                            {u.firstName} {u.lastName}
                        </button>
                    ))}
                </div>

                <div className="map-legend">
                    {Object.entries(ACTIVITY_COLORS).filter(([k]) => k !== 'other').map(([type, color]) => (
                        <span key={type} className="map-legend-item">
                            <span className="map-legend-dot" style={{ background: color }} />
                            {type}
                        </span>
                    ))}
                </div>
            </div>

            {loading && (
                <div className="map-overlay-message">Loading routes…</div>
            )}
            {error && (
                <div className="map-overlay-message map-overlay-error">{error}</div>
            )}
            {!loading && !error && routes.length === 0 && (
                <div className="map-overlay-message">No GPS routes found yet. Sync Strava activities or upload a GPX/FIT file when registering an activity.</div>
            )}

            <div ref={mapContainer} className="map-container" />
        </div>
    );
};

export default MapPage;
